/**
 * #owner-path-sweep — owner-path resolution for site-doc and report writes.
 *
 * Part 1 (commit ededbc0b): extended #sync-permission-fix (ca5eefb0) to EVERY
 * store writer targeting `users/{uid}/sites/...`:
 *
 * A company-shared site loaded via the fetchSites collectionGroup hop has
 * its Firestore doc under ANOTHER user's path. Writing it to the CURRENT
 * user's path targets a NON-EXISTENT doc; the sites `allow update` rule
 * then errors on `resource.data.get('locked', false)` (null resource) and
 * Firestore surfaces PERMISSION_DENIED for EVERY caller — including
 * platform admins and the in-app site owner.
 *
 * `SiteEntity._ownerUid` is the TRANSIENT in-memory annotation (never
 * persisted) stamped by `fetchSites` from the collectionGroup doc ref.
 * Own sites never carry it — `siteOwnerUid` falls back to the current uid,
 * so owner self-writes are byte-identical to the pre-sweep paths.
 *
 * Part 2 (this file, #owner-path-sweep-p2): same disease in the REPORT
 * collection. `fetchRagReports` stamps `_ownerUid` on company-shared reports
 * exactly as `fetchSites` does for sites. Every writer that targets
 * `users/{uid}/reports/{id}` must resolve through `reportOwnerUid`/
 * `reportOwnerPath` so the write lands on the doc that actually EXISTS.
 *
 * Evidence (SED amendments / revisions / signoffs at `users/{uid}/evidence/`):
 * rules currently have NO company-member write arm on that path — members can
 * only create/update there when they hold admin/analyst role (`canWrite()`).
 * The evidence writers are classified as LEAVE-BY-DESIGN pending a deliberate
 * rules extension; see the RULES GAP section in the part-2 final report.
 *
 * Simulations (`users/{uid}/simulations/`):
 * owner-only collection (rules: `allow read: if isOwner(userId) || isAdmin()`
 * with NO company arm, never fetched via collectionGroup). All sim writers
 * are LEAVE-BY-DESIGN.
 *
 * The path-specific rules (`match /users/{userId}/reports/{reportId}`) already
 * authorize company-tree members at the OWNER's path via the
 * `isInCompanyTree(getUserCompanyId())` WRITE arm — this is purely a
 * write-shape fix. NO rules change required for reports.
 */
import type { SiteEntity } from '../types/site';
import type { SiteRagReport } from '../types/siteRag';

/** Anything that may carry the transient `_ownerUid` annotation. */
export type SiteOwnerCarrier = Pick<SiteEntity, '_ownerUid'> | null | undefined;

/**
 * The uid whose `users/{uid}/sites/{siteId}` path actually HOLDS the site
 * doc. Falls back to `currentUid` for own sites (no `_ownerUid` stamp) and
 * for sites that could not be resolved from store state — in the fallback
 * case the write behaves exactly as it did pre-sweep.
 */
export function siteOwnerUid(site: SiteOwnerCarrier, currentUid: string): string {
  return site?._ownerUid ?? currentUid;
}

/** `users/{ownerUid}/sites` — the collection that actually holds the site doc. */
export function siteOwnerSitesPath(site: SiteOwnerCarrier, currentUid: string): string {
  return `users/${siteOwnerUid(site, currentUid)}/sites`;
}

/** `users/{ownerUid}/sites/{siteId}` — full site-doc path (subcollections hang off this). */
export function siteOwnerDocPath(site: SiteOwnerCarrier, currentUid: string, siteId: string): string {
  return `${siteOwnerSitesPath(site, currentUid)}/${siteId}`;
}

/**
 * Strip the TRANSIENT in-memory annotations (`_ownerUid`, `_sourceScope`)
 * from a site write payload. They must NEVER be persisted — a persisted
 * `_ownerUid` would survive refresh re-stamping and could redirect future
 * writes; a persisted `_sourceScope` breaks the mine/company filter model
 * (#79). Non-mutating: returns a shallow copy without the two keys.
 */
export function stripTransientSiteAnnotations<T extends Partial<SiteEntity>>(
  payload: T,
): Omit<
  T,
  | '_ownerUid'
  | '_sourceScope'
  | '_siteLayersCanonicalComplete'
  | '_siteLayersHydrationPending'
  | '_siteLayersHydrationError'
> {
  const {
    _ownerUid: _omitOwnerUid,
    _sourceScope: _omitSourceScope,
    _siteLayersCanonicalComplete: _omitCanonicalComplete,
    _siteLayersHydrationPending: _omitHydrationPending,
    _siteLayersHydrationError: _omitHydrationError,
    ...rest
  } = payload;
  return rest;
}

// ═══════════════════════════════════════════════════════════
// Part 2 — Report owner-path helpers (#owner-path-sweep-p2)
// ═══════════════════════════════════════════════════════════

/** Anything that may carry the transient `_ownerUid` report annotation. */
export type ReportOwnerCarrier = Pick<SiteRagReport, '_ownerUid'> | null | undefined;

/**
 * The uid whose `users/{uid}/reports/{reportId}` path actually HOLDS the
 * report doc. Falls back to `currentUid` for own reports (no `_ownerUid`
 * stamp) — own-report writes are byte-identical to the pre-sweep paths.
 */
export function reportOwnerUid(report: ReportOwnerCarrier, currentUid: string): string {
  return report?._ownerUid ?? currentUid;
}

/** `users/{ownerUid}/reports/{reportId}` — full Firestore doc path. */
export function reportOwnerDocPath(
  report: ReportOwnerCarrier,
  currentUid: string,
  reportId: string,
): string {
  return `users/${reportOwnerUid(report, currentUid)}/reports/${reportId}`;
}

/** `users/{ownerUid}/reports/{reportId}.json` — GCS object path for the report JSON payload. */
export function reportOwnerGcsPath(
  report: ReportOwnerCarrier,
  currentUid: string,
  reportId: string,
): string {
  return `users/${reportOwnerUid(report, currentUid)}/reports/${reportId}.json`;
}

/**
 * Strip the TRANSIENT `_ownerUid` annotation from a report write payload.
 * Must be called on EVERY object sent to Firestore / GCS — a persisted
 * `_ownerUid` would survive refresh re-stamping and redirect future writes
 * to the wrong path.  Non-mutating: returns a shallow copy without the key.
 */
export function stripTransientReportAnnotations<T extends Partial<SiteRagReport>>(
  payload: T,
): Omit<T, '_ownerUid' | '_frozenBranding'> {
  const { _ownerUid: _omit, _frozenBranding: _omitFrozenBranding, ...rest } = payload;
  void _omit;
  void _omitFrozenBranding;
  return rest;
}
